import { useEffect, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { SvgIcon } from "./ui/SvgIcon";
import closeIcon from "../assets/close-icon.svg?raw";
import { getThemeColors } from "../lib/theme";

interface ArticleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string | null;
  isDark: boolean;
}

export function ArticleDialog({
  isOpen,
  onClose,
  title,
  description,
  isDark,
}: ArticleDialogProps): JSX.Element {
  const { t } = useTranslation();
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return <></>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-pointer"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md animate-in fade-in duration-200" />

      {/* Dialog */}
      <div
        className={`relative w-full max-w-lg rounded-2xl border-[3px] border-ink shadow-board-lg cursor-default animate-in zoom-in-95 duration-200 ${
          isDark ? "bg-zinc-900" : "bg-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-0">
          <h2
            className={`font-serif text-2xl font-bold pr-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className={`shrink-0 p-1.5! ${getThemeColors(isDark).buttonSmall}`}
          >
            <SvgIcon markup={closeIcon} size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 pt-4 max-h-[70vh] overflow-y-auto">
          {description ? (
            <p
              className={`text-sm leading-relaxed ${isDark ? "text-zinc-400" : "text-gray-600"}`}
            >
              {description}
            </p>
          ) : (
            <p
              className={`text-sm italic ${isDark ? "text-zinc-600" : "text-gray-400"}`}
            >
              {t("components.articleDialog.noDescription")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
